<template>
  <!-- Add or edit collection dialog -->
  <Dialog
    :title="title"
    :visible="visible"
    :loading="loading"
    :appendToBody="true"
    :customClass="'collection-dialog'"
    @close="close"
    @cancel="close"
    @confirm="confirm"
  >
    <template #content>
      <ul>
        <el-form ref="formRef" :model="form" :rules="rules">
          <el-form-item prop="collectionName">
            <li>
              <p class="title-p">
                <a class="red-required">*</a>{{ t("upload.name") }}
              </p>
              <el-input
                ref="collectionNameInputRef"
                class="input"
                v-model="form.collectionName"
                :maxlength="MAX_COLLECTION_NAME_LENGTH"
                @blur="collectionNameInputBlur"
              />
            </li>
          </el-form-item>
          <el-form-item>
            <li>
              <p class="title-p">{{ t("upload.cover_image") }}</p>
              <div
                class="upload-cover-div"
                v-if="
                  !!uploadImageConfig &&
                  !!uploadImageConfig.fileUrl &&
                  !!uploadImageConfig.fileId
                "
              >
                <Gif
                  v-if="isGif"
                  :src="uploadImageConfig.fileUrl"
                  :ImageSize="ImageSize.Collection"
                />
                <img
                  v-else
                  :src="`${uploadImageConfig.fileUrl}${ImageSizeWH.Collection}`"
                />
                <el-upload
                  v-if="isGif"
                  ref="uploadRef"
                  class="upload-file-uploader column"
                  name="file"
                  :auto-upload="true"
                  :before-upload="beforeUpload"
                  :drag="false"
                  :limit="1"
                  :multiple="false"
                  :on-change="onChange"
                  :on-progress="handleProgress"
                  :show-file-list="false"
                  :accept="IMAGE_UPLOAD_ACCEPT_TYPES.JPG_PNG_WEBP_GIF"
                >
                  <span class="edit-span">
                    {{ t("upload.replace_image") }}
                  </span>
                </el-upload>
                <span
                  v-else
                  class="edit-span"
                  @click.stop="cropperVisible = true"
                >
                  {{ t("upload.edit") }}
                </span>
                <div class="close-btn" @click="deletePhoto">
                  <svg-icon name="icon_white_close" />
                </div>
              </div>
              <div
                v-else-if="
                  !!uploadImageConfig &&
                  !uploadImageConfig.fileUrl &&
                  !!uploadImageConfig.preViewUrl
                "
                class="upload-cover-div preView-div"
              >
                <img :src="uploadImageConfig.preViewUrl" />
                <FileProcess :percentage="uploadImageConfig.percentage" />
              </div>
              <div v-else>
                <el-upload
                  ref="uploadRef"
                  class="upload-file-uploader static"
                  name="file"
                  :auto-upload="true"
                  :before-upload="beforeUpload"
                  :drag="false"
                  :limit="1"
                  :multiple="false"
                  :on-change="onChange"
                  :on-progress="handleProgress"
                  :show-file-list="false"
                  :disabled="uploadLoading"
                  :accept="IMAGE_UPLOAD_ACCEPT_TYPES.JPG_PNG_WEBP_GIF"
                >
                  <div
                    class="add-content"
                    style="border: 1px dashed var(--color-brand)"
                  >
                    <div class="uploading-div" v-if="uploadLoading">
                      <img src="~/assets/images/loading_1.gif" />
                      <p>{{ t("upload.uploading") }}</p>
                    </div>
                    <template v-else>
                      <span class="add-icon-span">
                        <svg-icon name="upload-add" />
                      </span>
                      <span>
                        <p class="white-nowrap">
                          {{ t("upload.recommended_4_3_ratio") }}
                        </p>
                      </span>
                    </template>
                  </div>
                </el-upload>
                <p class="upload-file-tip-p">
                  png/jpg/webp/gif, ≤ {{ MAX_IMGAE_SIZE }} MB, 1200*900
                  {{ $t("upload.recommended_pic") }}
                </p>
              </div>
            </li>
          </el-form-item>
          <el-form-item>
            <li>
              <p class="title-p">{{ t("upload.intro") }}</p>
              <el-input
                ref="inputRef"
                class="input"
                type="textarea"
                :autosize="{ minRows: 2, maxRows: 4 }"
                v-model="form.collectionIntro"
                :showWordLimit="true"
                :maxlength="1000"
                @input="introInput"
                @blur="collectionIntroInputBlur"
              />
            </li>
          </el-form-item>
          <el-form-item>
            <li>
              <TagInput
                :recommendTags="recommendTags"
                :layout="TagInputLayout.HORIZONTAL"
                :width="'463px'"
                :maxW="'463px'"
                :initTags="tags"
                @change="tagsChange"
              />
            </li>
          </el-form-item>
          <li>
            <p class="title-p">{{ t("upload.public") }}</p>
            <div class="flex gap-12px">
              <el-switch
                v-model="collectionOpenStatus"
                :active-text="
                  collectionOpenStatus
                    ? t('upload.public')
                    : t('upload.private')
                "
              />
            </div>
          </li>
        </el-form>
      </ul>
    </template>
    <template #button>
      <div class="footer-button">
        <el-button class="gray-btn" @click="close">{{
          t("common.cancel")
        }}</el-button>
        <el-button
          class="purple-btn"
          :disabled="uploadLoading"
          :loading="loading"
          @click="confirm"
          >{{ t("common.confirm") }}</el-button
        >
      </div>
    </template>
  </Dialog>
  <!-- 裁剪图片窗口 -->
  <Cropper
    :title="t('upload.cropper_editor')"
    :visible="cropperVisible"
    :image="image"
    :appendToBody="true"
    :maxSingleFileSize="100"
    :fixed="true"
    :fixedNumber="[4, 3]"
    :autoCropWidth="400"
    :autoCropHeight="300"
    @cancel="cropperVisible = false"
    @close="cropperVisible = false"
    @ok="cropperDone"
  />
</template>

<script setup lang="ts">
const MAX_IMGAE_SIZE = 100; // 最大图片大小 100MB;
const MAX_COLLECTION_NAME_LENGTH = 80; // 合集名称最大长度 80;
import { nextTick, onMounted, ref, watch, type PropType } from "vue";
import { useUpload, IMAGE_UPLOAD_ACCEPT_TYPES } from "~/composables/useUpload";
import Gif from "~/components/Gif";
import Cropper from "~/components/Cropper";
import { getFileExtension } from "~/utils/file";
import FileProcess from "~/components/FileProcess";

const store = useCollectionDialogStore();

const { visible, formData, loading, isEdit } = storeToRefs(store);

const { t } = useI18n();
const { httpRequest, getCommonOptions } = useUpload();

const isGif = computed(
  () =>
    !!uploadImageConfig.value &&
    !!uploadImageConfig.value.fileUrl &&
    !!~uploadImageConfig.value.fileUrl.toLowerCase().indexOf("gif")
);

const inputRef = ref(null);

const formRef = ref(null);
const collectionNameInputRef = ref(null);
const form = reactive({
  collectionName: "",
  collectionIntro: "",
});
const tags = ref<string[]>([]);
const collectionOpenStatus = ref(false); // 默认是不公开;
// tag change;
const tagsChange = (val: string[]) => {
  tags.value = val;
};
const title = computed(() =>
  isEdit.value ? t("upload.edit_collection") : t("upload.new_colleaction")
);
const confirm = () => {
  if (!formRef.value) return;
  formRef.value?.validate((valid: boolean) => {
    if (valid) {
      // 新建集合默认是有效的合集collectionAvailable
      const emitsData = {
        collectionName: form.collectionName.trim(),
        collectionIntro: form.collectionIntro.trim(),
        collectionOpenStatus: collectionOpenStatus.value,
        tags: tags.value,
        coverImgFileId: uploadImageConfig.value?.fileId || 0,
        coverImgUrl: uploadImageConfig.value?.fileUrl,
        collectionAvailable: 1,
        id: formData.value.id,
      };
      image.value = emitsData.coverImgUrl;
      if (!emitsData.collectionName) return;
      // 执行回调
      store.handleConfirm(emitsData);
    }
  });
};
const close = () => {
  store.handleClose();
};
// 获取推荐标签;
const recommendTags = ref([]);
// 初始化input控件数据;
const initForm = () => {
  uploadImageConfig.value = formData.value.uploadImageConfig ?? null;
  form.collectionName = formData.value.collectionName ?? "";
  form.collectionIntro = formData.value.collectionIntro ?? "";
  tags.value = isEdit.value ? formData.value.tags ?? [] : [];
  collectionOpenStatus.value = formData.value.collectionOpenStatus ?? false;
  image.value = uploadImageConfig.value.fileUrl ?? "";
};

watch(
  () => visible.value,
  (value: boolean) => {
    initForm();
    value && handleTextCount();
  }
);
// #region about upload cover and cropper image;
const uploadLoading = ref(false);
const beforeUpload = (file) => {
  console.log("beforeUpload", file);
  if (!checkFileType(file)) {
    uploadLoading.value = false;
    uploadImageConfig.value = null;
    useToast.error(t("upload.unsupported_file_format"));
    return false;
  }
  if (file.size > MAX_IMGAE_SIZE * 1024 * 1024) {
    uploadLoading.value = false;
    uploadImageConfig.value = null;
    useToast.error(
      t("upload.uploaded_file_exceeds_upper_limit", {
        value: `${MAX_IMGAE_SIZE}MB`,
      })
    );
    return false;
  }
  return true;
};
// check file type;
const checkFileType = (file: UploadRawFile) => {
  const fileExtension = getFileExtension(file.name);
  return IMAGE_UPLOAD_ACCEPT_TYPES.JPG_PNG_WEBP_GIF_TYPES.some((type) => {
    if (fileExtension.toLowerCase() === type.toLowerCase()) return true;
    return false;
  });
};
const image = ref("");
const uploadRef = ref(null);

const handleProgress = (event, file, fileListProcess) => {
  uploadImageConfig.value = {
    ...uploadImageConfig.value,
    percentage: Number((event.progress * 100).toFixed(2)),
  };
};

const getPreViewUrl = async (file) => {
  uploadImageConfig.value = {
    ...uploadImageConfig.value,
    preViewUrl: await getImagePreViewUrl(file.raw),
  };
};
const onChange = async (file) => {
  if (!beforeUpload(file)) return;
  try {
    uploadLoading.value = true;
    await getPreViewUrl(file);
    const postOptions = getCommonOptions(
      file.raw,
      () => {},
      (process) => {
        handleProgress(process, file, formData.value.fileList);
      }
    );
    const postResponse = await httpRequest(postOptions);
    uploadLoading.value = false;
    uploadRef.value && uploadRef.value.clearFiles();
    if (!!postResponse) {
      const config = {
        fileId: postResponse.fileId,
        fileName: postResponse.name,
        fileSize: postResponse.size,
        fileExt: postResponse.type.replace("image/", ""),
        fileUrl: postResponse.url,
      };
      uploadImageConfig.value = config;
      image.value = config.fileUrl;
      !isGif.value && (cropperVisible.value = true);
    }
  } catch (error) {
    console.log("uploadChange error :", error);
    uploadLoading.value = false;
  }
};

const cropperVisible = ref(false);
const uploadImageConfig = ref();
// 裁剪完成;
const cropperDone = (data: any) => {
  uploadImageConfig.value = data;
  image.value = data.fileUrl;
  cropperVisible.value = false;
};
const deletePhoto = () => {
  uploadImageConfig.value = null;
  image.value = "";
};
// #endregion ;
// blur;
const collectionNameInputBlur = () => {
  form.collectionName = form.collectionName.trim();
};
const collectionIntroInputBlur = () => {
  form.collectionIntro = form.collectionIntro.trim();
};
const rules = computed(() => {
  return {
    collectionName: [{ validator: validateName, trigger: "blur" }],
  };
});
const validateName = (rule, value, callback) => {
  if (!value || value.trim() === "") {
    callback(new Error(`${t("prompt.this_field_cannot_be_empty")}`));
  } else {
    callback();
  }
};
// 处理文本字数限制 字符数/1k;
const handleTextCount = () => {
  nextTick(() => {
    if (!document) return;
    const textareaMaxlengthDoc =
      document.getElementsByClassName("el-input__count");
    if (!textareaMaxlengthDoc || textareaMaxlengthDoc.length === 0) return;
    const origiText = textareaMaxlengthDoc[0].innerText;
    textareaMaxlengthDoc[0].innerText = `${
      Number(origiText.split("/")[0]) === 1000 ? "1K" : origiText.split("/")[0]
    } / 1K`;
  });
};

const introInput = () => {
  handleTextCount();
  handleScrollToBottom(inputRef.value?.textarea);
};
</script>

<style lang="scss" scoped>
@use "AddCollectionDialog.scss";
</style>
<style lang="scss">
.collection-dialog {
  --el-overlay-color-lighter: #00000040 !important;
}
</style>
